import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent, MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject, Subscription } from 'rxjs';
import { VerificationPackagesService } from '../services/verification-packages.service';
import { VerificationPackageDetail, VerificationPackageRow, verificationPackageStatusIdToStatus } from '../models/VerificationPackage';
import { VerificationPackageBasicInfoDialogComponent } from './components/basic-info-dialog/basic-info-dialog.component';
import { CheckTypeDialogComponent } from './components/check-type-dialog/check-type-dialog.component';
import { DeletePolicyUnderwriterDialogComponent } from '../../policies/policy-detail/tabs/policy-underwriters/components/delete-policy-underwriter-dialog/delete-policy-underwriter-dialog.component';
import { DeactivatePackageDialogComponent } from './components/deactivate-package-dialog/deactivate-package-dialog.component';
import { getVerificationCheckTypeRowFromPackage, VerificationCheckTypesColumns, VerificationPackageCheckType, VerificationPackageCheckTypeByIdResponse } from '../models/VerificationCheckType';
import { VerificationPartner } from '../models/VerificationPartner';
import { Logger } from '../../../infrastructure/logging/logger';
import { getColor, ColorProp } from '../../../layout/Colors';
import { DetailPair } from '../../../shared/components/detail-info-card/DetailPair';
import { NotificationService } from '../../../utilities/PortalNotificationUtility';
import { DetailInfoCardComponent } from '../../../shared/components/detail-info-card/detail-info-card.component';
import { DataTableComponent } from '../../../shared/components/data-table/data-table.component';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';

@Component({
  selector: 'app-verification-package-detail',
  templateUrl: './verification-package-detail.component.html',
  styleUrls: ['./verification-package-detail.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatCard,
    MatTabsModule,
    DetailInfoCardComponent,
    DataTableComponent,
    CommonModule,
    RouterModule,
    MatProgressSpinner,
    MatButtonModule,
    BackButtonComponent
  ]
})
export class VerificationPackageDetailComponent implements OnInit {

  private routeSub!: Subscription;
  @ViewChild(MatTabGroup) matTabGroup!: MatTabGroup;

  basicInfo: DetailPair[] = [];
  description!: DetailPair;
  dataSource = new MatTableDataSource<VerificationPackageRow>();
  displayedColumns = VerificationCheckTypesColumns;
  tableLength = 0;
  tabIndex: number = 0;

  loading = new BehaviorSubject<boolean>(false);
  verificationPackageId!: string;
  verificationPackage!: VerificationPackageDetail;
  checkTypes: VerificationPackageCheckType[] = [];
  verificationCheckTypes: VerificationPackageCheckTypeByIdResponse[] = [];
  verificationPartners: VerificationPartner[] = [];

  constructor(
    private verificationPackagesService: VerificationPackagesService,
    private route: ActivatedRoute,
    private router: Router,
    private logService: Logger,
    private notificationService: NotificationService,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.routeSub = this.route.params.subscribe(params => {
      this.verificationPackageId = params['id'];
      this.loadPage();
    });

    this.tabIndex = parseInt(this.route.snapshot.queryParamMap.get('tab') || '', 10);
    if (isNaN(this.tabIndex) || this.tabIndex < 0) {
      this.tabIndex = 0;
    }
  }

  async loadPage() {
    try {
      this.loading.next(true);
      await Promise.all([
        this.getVerificationPackageById(this.verificationPackageId),
        this.getVerificationCheckTypesById(this.verificationPackageId),
        this.getVerificationCheckTypes(),
        this.getVerificationPartners()
      ]);

      this.populateBasicInfo();
      this.populateCheckTypesDatasource();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Verification Package details', 'Error while retrieving Verification Package details');
    } finally {
      this.loading.next(false);
    }
  }

  async getVerificationPackageById(id: string) {
    if (!id) {
      return;
    }

    try {
      this.verificationPackage = await this.verificationPackagesService.getVerificationPackageDetail(id);
    } catch (e) {
      this.logService.handleError(e, `An error occurred while retrieving Verification Package detail page data for ID: ${id}`, `An error occurred while retrieving Verification Package detail page data for ID: ${id}`);
      return;
    }
    this.populateBasicInfo();
  }

  async getVerificationCheckTypes() {
    try {
      this.checkTypes = await this.verificationPackagesService.getVerificationCheckTypes();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Verification Check Types', 'Error while retrieving Verification  Check Types');
      return;
    }
  }

  async getVerificationCheckTypesById(id: string) {
    if (!id) {
      return;
    }

    try {
      this.verificationCheckTypes = await this.verificationPackagesService.getVerificationPackageCheckTypesById(Number(id));
    } catch (e) {
      this.logService.handleError(e, `An error occurred while retrieving Verification Check Type for ID: ${id}`, `An error occurred while retrieving Verification Check Type for ID: ${id}`);
      return;
    }
    this.populateCheckTypesDatasource();
  }

  async getVerificationPartners() {
    try {
      this.verificationPartners = await this.verificationPackagesService.getVerificationPartners();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Verification Partners', 'Error while retrieving Verification Partners');
      return;
    }
  }

  populateBasicInfo() {
    if (this.verificationPackage) {
      this.basicInfo = [];
      this.basicInfo.push(new DetailPair("Package ID", this.verificationPackage.id));
      this.basicInfo.push(new DetailPair("Status", verificationPackageStatusIdToStatus(this.verificationPackage.status_id)));
      this.basicInfo.push(new DetailPair("Description", this.verificationPackage.description));
    }
  }

  populateCheckTypesDatasource() {
    if (!this.verificationCheckTypes) {
      this.notificationService.showNotification({ message: 'No verification check types found' });
      return;
    }

    const mappedVerificationCheckTypes = this.verificationCheckTypes.map((verificationPackage: any) => {
      return getVerificationCheckTypeRowFromPackage(verificationPackage);
    });
    this.dataSource = new MatTableDataSource(mappedVerificationCheckTypes);
    this.tableLength = this.dataSource.data.length;
  }

  onEditVerificationPackageClick() {
    this.openEditBasicInfoDialog();
  }

  openEditBasicInfoDialog() {
    const basicInfoDialogRef = this.dialog.open(VerificationPackageBasicInfoDialogComponent, {
      data: this.verificationPackage,
      disableClose: true,
      width: '70%',
      maxHeight: '400px'
    });
    basicInfoDialogRef.afterClosed().subscribe((payload) => {
      if (payload) {
        this.verificationPackagesService.updateVerificationPackage(this.verificationPackageId, payload).then(_ => {
          this.loadPage();
        });
      }
    });
  }

  onUpdateVerificationPackageStatusClick() {
    this.openUpdatePackageStatusDialog();
  }

  openUpdatePackageStatusDialog() {
    const deactivateDialogRef = this.dialog.open(DeactivatePackageDialogComponent, {
      width: '55%',
      height: '210px',
      disableClose: false,
      data: {
        component: 'Verification Package',
        verification_package: this.verificationPackage
      }
    });
    deactivateDialogRef.afterClosed().subscribe(async statusId => {
      if (statusId > 0) {
        let payload = {
          id: this.verificationPackageId,
          status_id: statusId
        }

        this.verificationPackagesService.updateVerificationPackageStatus(this.verificationPackageId, payload).then(response => {
          if (response) {
            this.navigateToVerificationPackagesPage();
          }
        });
      }
    });
  }

  onAddCheckTypeClick() {
    this.openAddCheckTypeDialog();
  }

  openAddCheckTypeDialog() {
    const checkTypeDialogRef = this.dialog.open(CheckTypeDialogComponent, {
      disableClose: true,
      width: '55%',
      // maxHeight: '500px',
      data: {
        checkTypes: this.checkTypes,
        partners: this.verificationPartners,
        verificationCheckTypes: this.verificationCheckTypes
      }
    });
    checkTypeDialogRef.afterClosed().subscribe((data) => {
      if (data) {
        data.check_types.forEach((check_type: any) => {
          let payload = {
            verification_check_type_id: check_type.id.toString(),
            verification_partner_id: data.partner_id.toString(),
            verification_package_id: this.verificationPackageId
          }
  
          this.verificationPackagesService.createVerificationCheckType(payload).then(_ => {
            this.loadPage();
          });
        });
      }
    });
  }

  onDeleteVerificationCheckTypeClick(row: any) {
    this.openDeleteCheckTypeDialog(row);
  }

  openDeleteCheckTypeDialog(checkType: any) {
    const deleteDialogRef = this.dialog.open(DeletePolicyUnderwriterDialogComponent, {
      width: '50vw',
      maxHeight: '265px',
      disableClose: false,
      data: 'Check Type'
    });
    deleteDialogRef.afterClosed().subscribe(res => {
      if (res > 0) {
        this.verificationPackagesService.deleteVerificationPackageType(checkType.id).then(_ => {
          this.loadPage();
        });
      }
    });
  }

  navigateToVerificationPackagesPage() {
    this.router.navigate([`/verification-packages`]);
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent) {
    this.tabIndex = tabChangeEvent.index;
    this.router.navigate([], { relativeTo: this.route });
  }

  getCheckTypeStatusColor(status: string): string {
    if (status === "Active"){
      return getColor(ColorProp.PropertyStatusActive);
    } else {
      return '#d3d3d3';
    }
  }
}